import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@shadcn/lib/utils"

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				default: "bg-primary text-white hover:bg-teal-600",
				destructive:
					"bg-destructive text-white hover:bg-error-600 focus-visible:ring-error-300 dark:focus-visible:ring-error-300 dark:bg-error-500 dark:hover:bg-error-600",
				outline:
					"border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
				secondary:
					"bg-secondary text-secondary-foreground  hover:bg-teal-200 dark:bg-secondary dark:text-teal-700 dark:hover:bg-teal-200 dark:hover:text-secondary-foreground",
				ghost: "bg-primary/00 hover:bg-teal-100 text-teal-600 hover:text-teal-700 dark:hover:bg-primary/10",
				link: "text-info-500 hover:text-info-600 underline-offset-4 hover:underline dark:hover:text-info-600",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
				lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
				icon: "size-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
)

const Button = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
	const Comp = asChild ? Slot : "button"

	return <Comp ref={ref} data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />
})

Button.displayName = "Button"

export { Button, buttonVariants }
